import { Box, MenuItem, Select, type SxProps } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { type FC } from "react";
import {
  fromISO8601_DATE,
  toISO8601_DATE,
  type ISO8601_DATE,
} from "../../../../../../utils/iso8601";
import type { UsableAccountPersonField } from "../../../../server/supabase-segment-field-service";
import type { Condition } from "../../types";

export const DateSelectField: FC<{
  condition: Condition;
  handleFieldChange: (value: string[]) => void;
  error?: string;
  disabled?: boolean;
  segmentField?: UsableAccountPersonField;
  hidden?: boolean;
  sx?: SxProps;
}> = ({ condition, handleFieldChange, error, disabled, segmentField, hidden, sx }) => {
  return (
    <Box hidden={hidden}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <DatePicker
          value={condition.values[0] ? fromISO8601_DATE(condition.values[0] as ISO8601_DATE) : null}
          onChange={(value: Date | null) => {
            handleFieldChange(value ? [toISO8601_DATE(value)!] : []);
          }}
          slotProps={{
            textField: {
              label: segmentField?.display_name,
              error: Boolean(error),
              helperText: error,
              variant: "standard",
              hidden: hidden,
              size: "small",
            },
          }}
          disabled={disabled}
          sx={sx}
        />
      </LocalizationProvider>
    </Box>
  );
};

export const MonthDaySelectField: FC<{
  condition: Condition;
  handleFieldChange: (value: string[]) => void;
  disabled?: boolean;
  hidden?: boolean;
}> = ({ condition, handleFieldChange, disabled, hidden }) => {
  return (
    <Box hidden={hidden}>
      <Select
        disabled={disabled}
        variant="outlined"
        size="small"
        sx={{ width: 160, mr: 1 }}
        value={monthsIn(condition.values[0])}
        onChange={(event) => {
          const r = monthDayToRegExpExecArray(condition.values[0]);
          const value = toMonthDay(event.target.value, r && r[2] ? r[2] : 1);
          handleFieldChange([value]);
        }}
      >
        {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
          <MenuItem value={month} key={month}>
            {months[month - 1]!}
          </MenuItem>
        ))}
      </Select>
      <Select
        disabled={disabled}
        variant="outlined"
        sx={{ width: 80 }}
        value={daysIn(condition.values[0])}
        size="small"
        onChange={(event) => {
          const r = monthDayToRegExpExecArray(condition.values[0]);
          const value = toMonthDay(r && r[1] ? r[1] : 1, event.target.value);
          handleFieldChange([value]);
        }}
      >
        {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
          <MenuItem value={day} key={day}>
            {day}
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
};

function monthDayToRegExpExecArray(monthDay?: string): RegExpExecArray | null {
  if (!monthDay) return null;

  return new RegExp(/\((\d+),(\d+)\)/g).exec(monthDay);
}

function daysIn(monthDay?: string): number {
  if (!monthDay) return 1;

  const r = monthDayToRegExpExecArray(monthDay);
  return r && r[2] ? parseInt(r[2]) : 1;
}

function monthsIn(monthDay?: string): number {
  if (!monthDay) return 1;

  const r = monthDayToRegExpExecArray(monthDay);
  return r && r[1] ? parseInt(r[1]) : 1;
}

function toMonthDay(months: string | number, days: string | number): string {
  return `(${months},${days})`;
}

const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
